import { useState } from "react";
import { usePackageTree } from "@/hooks/usePackageTree";
import { AnimatePresence } from "framer-motion";
import { Ticket, XCircle, Info, FileSpreadsheet, CalendarDays, Search } from "lucide-react";
import type { PackageItem } from "@/types/package";
import InfoKonvensiFeesModal from "./InfoKonvensiFessModal";

type Props = {
    periodId: number;
};

export default function PicPackageInfoList({ periodId }: Props) {
    const { data: packageTree, isLoading, isError } = usePackageTree(periodId, true);
    const [detailPkg, setDetailPkg] = useState<PackageItem | null>(null);

    if (isLoading) {
        return (
            <div className="flex flex-col gap-4 animate-pulse">
                {[1, 2].map((i) => (
                    <div key={i} className="h-40 bg-slate-200 dark:bg-slate-800 rounded-2xl" />
                ))}
            </div>
        );
    }

    if (isError || !packageTree) {
        return <div className="text-sm text-red-500 text-center">Gagal memuat katalog paket.</div>;
    }

    return (
        <section className="mx-auto max-w-7xl antialiased space-y-6 mb-10 mt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-blue-50/50 dark:bg-slate-800/50 p-5 rounded-2xl border border-blue-100 dark:border-slate-700">
                <div className="space-y-1">
                    <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                        <FileSpreadsheet className="w-5 h-5 text-blue-500" />
                        Katalog Paket Kegiatan
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 max-w-2xl">
                        Gunakan <strong>ID Paket</strong> di bawah ini saat mengisi file Excel pendaftaran kolektif. Pastikan kuota masih tersedia.
                    </p>
                </div>
            </div>

            <div className="flex flex-col gap-4">
                {packageTree.map((pkg: PackageItem) => {
                    const price = Number(pkg.price);
                    const children = pkg.children ?? [];
                    const hasChildren = children.length > 0;
                    const isFree = price === 0 && !hasChildren;
                    const isSoldOut = pkg.is_quota === 1 && pkg.available_quota <= 0;

                    // Format tanggal acara jika tersedia
                    let eventDate = "Tanggal tidak tersedia";
                    if (pkg.start_date && pkg.end_date) {
                        if (pkg.start_date === pkg.end_date) {
                            eventDate = new Date(pkg.start_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
                        } else {
                            const start = new Date(pkg.start_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' });
                            const end = new Date(pkg.end_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
                            eventDate = `${start} - ${end}`;
                        }
                    }

                    return (
                        <div
                            key={pkg.id}
                            className={`group relative flex flex-col md:flex-row overflow-hidden rounded-2xl border transition-all duration-300
                                ${isSoldOut
                                    ? "border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/50 opacity-90"
                                    : "border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 hover:shadow-lg hover:border-blue-200 dark:hover:border-blue-900"
                                }`}
                        >
                            {/* Sold Out Badge */}
                            {isSoldOut && (
                                <div className="absolute top-4 left-4 z-20 flex items-center gap-1.5 rounded-full bg-rose-500 px-3 py-1 text-[10px] font-bold text-white shadow-lg shadow-rose-500/40 uppercase tracking-widest">
                                    <XCircle className="h-3 w-3" />
                                    <span>Habis</span>
                                </div>
                            )}

                            {/* Image Section - Sisi Kiri */}
                            <div className="relative h-48 md:h-auto md:w-64 shrink-0 overflow-hidden">
                                <img
                                    src={pkg.photo_url || '/placeholder.jpg'}
                                    alt={pkg.name}
                                    className={`h-full w-full object-cover transition-transform duration-700 ${isSoldOut ? 'grayscale contrast-75 opacity-80' : 'group-hover:scale-110'}`}
                                />
                                <div className={`absolute inset-0 transition-opacity duration-300 ${isSoldOut ? 'bg-slate-900/20' : 'bg-gradient-to-t from-black/50 to-transparent'}`} />
                                {!(hasChildren && price === 0) && (
                                    <div className="absolute bottom-3 left-3 z-10">
                                        <div className="text-[10px] font-mono font-bold text-white bg-blue-600/90 backdrop-blur-sm px-2 py-1 rounded shadow-sm border border-blue-400/30">
                                            ID Paket: {pkg.id}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Content Section - Tengah */}
                            <div className="p-5 flex-1 flex flex-col justify-between">
                                <div className="space-y-3">
                                    <div>
                                        <h4 className={`text-lg font-bold transition-colors duration-300 ${isSoldOut ? "text-slate-500 dark:text-slate-400" : "text-slate-900 dark:text-white"}`}>
                                            {pkg.name}
                                        </h4>
                                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-500 dark:text-slate-400">
                                            <CalendarDays className="w-3.5 h-3.5" />
                                            <span>{eventDate}</span>
                                        </div>
                                        <p className="text-sm font-medium text-slate-500 dark:text-slate-400 line-clamp-2 mt-2">
                                            {pkg.description}
                                        </p>
                                    </div>

                                    {/* Info Kuota */}
                                    {pkg.is_quota === 1 && (
                                        <div className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 border w-fit ${isSoldOut ? "bg-rose-50 border-rose-200 dark:bg-rose-500/10 dark:border-rose-500/20" : "bg-orange-50 border-orange-200 dark:bg-orange-500/10 dark:border-orange-500/20"}`}>
                                            <Ticket className={`h-4 w-4 ${isSoldOut ? 'text-rose-500' : 'text-orange-500'}`} />
                                            <span className={`text-xs font-bold uppercase tracking-wide ${isSoldOut ? 'text-rose-600 dark:text-rose-400' : 'text-orange-600 dark:text-orange-400'}`}>
                                                Sisa Kuota: {pkg.available_quota} / {pkg.quota}
                                            </span>
                                        </div>
                                    )}

                                    {/* Sub-packages for PIC to see codes */}
                                    {hasChildren && (
                                        <div className="mt-4 p-4 bg-slate-50/80 dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-slate-800">
                                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">Pilihan Sub-Paket (Gunakan ID Ini)</div>
                                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                                                {pkg.children?.map(child => {
                                                    const childSoldOut = child.is_quota === 1 && child.available_quota <= 0;
                                                    return (
                                                        <div key={child.id} className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 text-xs p-3.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm hover:border-blue-300 dark:hover:border-blue-700 transition-colors">
                                                            <div className="flex flex-col">
                                                                <span className="font-mono font-black text-blue-600 dark:text-blue-400 text-sm">ID: {child.id} <span className="font-normal text-xs text-slate-400">({child.code})</span></span>
                                                                <span className={`font-bold mt-0.5 ${childSoldOut ? 'text-slate-400 line-through' : 'text-slate-700 dark:text-slate-200'}`}>{child.name}</span>
                                                            </div>
                                                            <div className="flex items-center justify-between sm:justify-end gap-3">
                                                                {child.is_quota === 1 && (
                                                                    <span className={`font-bold px-2.5 py-1 rounded-md text-[10px] uppercase shrink-0 ${childSoldOut ? 'bg-rose-100 text-rose-600' : 'bg-orange-100 text-orange-600'}`}>
                                                                        {childSoldOut ? 'Habis' : `${child.available_quota} sisa`}
                                                                    </span>
                                                                )}
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setDetailPkg(child);
                                                                    }}
                                                                    className={`group/subbtn flex items-center justify-center w-8 h-8 rounded-full transition-all shadow-sm
                                                                        ${childSoldOut
                                                                            ? "bg-slate-100 text-slate-400 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-500 cursor-not-allowed"
                                                                            : "bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-slate-600 hover:bg-blue-600 hover:text-white hover:border-blue-600 dark:hover:bg-blue-600"
                                                                        }`}
                                                                    title="Lihat Detail Sub-Paket"
                                                                >
                                                                    <Search className="w-4 h-4 transition-transform group-hover/subbtn:scale-110" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Aksi & Harga Section - Sisi Kanan (Disembunyikan jika ada sub-paket) */}
                            {!hasChildren && (
                                <div className="p-5 md:w-48 shrink-0 flex flex-row md:flex-col items-center justify-between border-t md:border-t-0 md:border-l border-slate-100 dark:border-slate-700/50 bg-slate-50/50 dark:bg-slate-800/30 md:items-end">
                                    <div className="flex flex-col items-start md:items-end">
                                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Harga Paket</span>
                                        <span className={`text-xl font-black transition-colors ${isSoldOut ? 'text-slate-400 dark:text-slate-500 line-through' : 'text-emerald-600 dark:text-emerald-400'}`}>
                                            {isFree ? "GRATIS" : `Rp ${price.toLocaleString("id-ID")}`}
                                        </span>
                                    </div>

                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setDetailPkg(pkg);
                                        }}
                                        className={`group/btn mt-0 md:mt-auto flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-[11px] font-bold transition-all duration-300 border 
                                            ${isSoldOut
                                                ? "bg-slate-100 dark:bg-slate-800/50 text-slate-500 border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-800"
                                                : "bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow-blue-500/20"}`}
                                    >
                                        <Info className="h-4 w-4 transition-transform group-hover/btn:scale-110" />
                                        <span className="tracking-widest uppercase">Lihat Detail</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <AnimatePresence>
                {detailPkg && (
                    <InfoKonvensiFeesModal
                        pkg={detailPkg}
                        onClose={() => setDetailPkg(null)}
                    />
                )}
            </AnimatePresence>
        </section>
    );
}
